import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Bookings from './pages/Bookings';
import Invoices from './pages/Invoices';
import Services from './pages/Services';
import Navigation from './components/Navigation';
import PractitionerDashboard from './pages/PractitionerDashboard';
import PractitionerServices from './pages/PractitionerServices';
import PractitionerAvailability from './pages/PractitionerAvailability';
import PractitionerCRM from './pages/PractitionerCRM';
import PractitionerEmailTemplates from './pages/PractitionerEmailTemplates';
import PractitionerCampaigns from './pages/PractitionerCampaigns';
import PractitionerReports from './pages/PractitionerReports';
import PractitionerProfile from './pages/PractitionerProfile';
import PractitionerInvoices from './pages/PractitionerInvoices';
import ShortURLGenerator from './pages/ShortURLGenerator';
import ClientBookingFlow from './pages/ClientBookingFlow';
import ShortURLLanding from './pages/ShortURLLanding';
import BookingDetail from './pages/BookingDetail';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminServices from './pages/AdminServices';
import AdminCoupons from './pages/AdminCoupons';
import AdminBookings from './pages/AdminBookings';
import Profile from './pages/Profile';
import { PrivacyPolicy, TermsAndConditions } from './pages/Legal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function RequireAdmin({ user, children }) {
  if (user?.role !== 'admin') return <Navigate to="/" />;
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    }
  }, [token]);

  const handleLogin = (loginData) => {
    setToken(loginData.token);
    setUser(loginData.user);
    localStorage.setItem('token', loginData.token);
    localStorage.setItem('user', JSON.stringify(loginData.user));
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  if (!token) {
    return (
      <Router>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login onLogin={handleLogin} apiUrl={API_URL} />} />
          <Route path="/register" element={<Register onRegister={handleLogin} apiUrl={API_URL} />} />
          <Route path="/register-via-link" element={<ShortURLLanding apiUrl={API_URL} onLogin={handleLogin} />} />
          <Route path="/legal/privacy" element={<PrivacyPolicy />} />
          <Route path="/legal/terms" element={<TermsAndConditions />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <div className="app">
        <Navigation user={user} onLogout={handleLogout} />
        <div className="main-content">
          <Routes>
            <Route path="/" element={<Dashboard user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/bookings" element={<Bookings user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/bookings/:id" element={<BookingDetail user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/invoices" element={<Invoices user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/services" element={<Services user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/profile" element={<Profile user={user} />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/terms" element={<TermsAndConditions />} />

            {/* Practitioner routes */}
            <Route path="/practitioner/dashboard" element={<PractitionerDashboard user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/practitioner/services" element={<PractitionerServices user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/practitioner/availability" element={<PractitionerAvailability user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/practitioner/crm" element={<PractitionerCRM user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/practitioner/email-templates" element={<PractitionerEmailTemplates user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/practitioner/campaigns" element={<PractitionerCampaigns user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/practitioner/reports" element={<PractitionerReports user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/practitioner/short-urls" element={<ShortURLGenerator user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/practitioner/profile" element={<PractitionerProfile user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/practitioner/invoices" element={<PractitionerInvoices user={user} apiUrl={API_URL} token={token} />} />

            {/* Client booking flow */}
            <Route path="/booking" element={<ClientBookingFlow user={user} apiUrl={API_URL} token={token} />} />
            <Route path="/register-via-link" element={<ShortURLLanding apiUrl={API_URL} onLogin={handleLogin} />} />

            {/* Superuser routes */}
            <Route path="/admin" element={<RequireAdmin user={user}><AdminDashboard apiUrl={API_URL} token={token} /></RequireAdmin>} />
            <Route path="/admin/users" element={<RequireAdmin user={user}><AdminUsers apiUrl={API_URL} token={token} /></RequireAdmin>} />
            <Route path="/admin/services" element={<RequireAdmin user={user}><AdminServices apiUrl={API_URL} token={token} /></RequireAdmin>} />
            <Route path="/admin/coupons" element={<RequireAdmin user={user}><AdminCoupons apiUrl={API_URL} token={token} /></RequireAdmin>} />
            <Route path="/admin/bookings" element={<RequireAdmin user={user}><AdminBookings apiUrl={API_URL} token={token} /></RequireAdmin>} />

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

export default App;
